/**
 * Repairs API Client — Landlord Portal
 */

import { z } from 'zod';
import {
  RepairRequestResponseSchema,
  TimelineItemResponseSchema,
  RepairPartySchema,
  RepairPartiesResponseSchema,
  PostRepairPartyMessageBodySchema,
  RepairPartyMessageResponseSchema,
  RepairMessageResponseSchema,
  RepairListRowResponseSchema,
  type ListAttachmentsResponse,
  type RepairThreadReadsResponse,
  type MarkThreadReadResponse,
} from '@resida/api-types/repair';
import { apiFetch } from '../lib/api';

// Derived from RepairRequestResponseSchema in @resida/api-types/repair
export type RepairRequest = z.infer<typeof RepairRequestResponseSchema>;

// RA-UNREAD-LIST — a list row is the repair plus its total unread count across threads.
export type RepairListRow = z.infer<typeof RepairListRowResponseSchema>;

// RA-COMPOSE-PARTY, RA-COMPOSE-PARTIES — derived from @resida/api-types/repair
export type RepairParty = z.infer<typeof RepairPartySchema>;
export type RepairPartiesResponse = z.infer<typeof RepairPartiesResponseSchema>;
export type PostRepairPartyMessageBody = z.infer<typeof PostRepairPartyMessageBodySchema>;
export type RepairPartyMessageResponse = z.infer<typeof RepairPartyMessageResponseSchema>;

// Derived from RepairMessageResponseSchema in @resida/api-types/repair — not hand-declared.
export type RepairMessage = z.infer<typeof RepairMessageResponseSchema>;

/**
 * One entry in the unified case timeline (US-20, US-21).
 * Backed by `case_events` rows — single shape per event regardless of type.
 * Type-specific UI is selected by `eventType` at render time.
 * Derived from TimelineItemResponseSchema in @resida/api-types/repair
 */
export type TimelineItem = z.infer<typeof TimelineItemResponseSchema>;

export async function listRepairs(params?: {
  status?: string;
  propertyId?: string;
}): Promise<{ repairs: RepairListRow[] }> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.propertyId) query.set('propertyId', params.propertyId);
  const qs = query.toString();
  return apiFetch(`/landlord/api/repairs${qs ? `?${qs}` : ''}`);
}

export async function getRepair(id: string): Promise<RepairRequest> {
  return apiFetch(`/landlord/api/repairs/${id}`);
}

// RA-UNREAD-DEF / RA-UNREAD-MARK — per-thread unread counts and the mark-read write.
export async function getThreadReads(id: string): Promise<RepairThreadReadsResponse> {
  return apiFetch(`/landlord/api/repairs/${id}/thread-reads`);
}

export async function markThreadRead(id: string, threadKey: string): Promise<MarkThreadReadResponse> {
  return apiFetch(`/landlord/api/repairs/${id}/thread-reads`, {
    method: 'POST',
    body: JSON.stringify({ threadKey }),
  });
}

export async function getRepairTimeline(id: string): Promise<{ items: TimelineItem[] }> {
  return apiFetch(`/landlord/api/repairs/${id}/timeline`);
}

export async function closeRepair(
  id: string,
  body: { reason?: string },
): Promise<RepairRequest> {
  return apiFetch(`/landlord/api/repairs/${id}/close`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function postRepairMessage(
  id: string,
  body: { content: string },
): Promise<{ message: RepairMessage }> {
  return apiFetch(`/landlord/api/repairs/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// RA-COMPOSE-PARTY — the composer's "A party on this case" mode (RepairComposer.tsx).

export async function getRepairParties(id: string): Promise<RepairPartiesResponse> {
  return apiFetch(`/landlord/api/repairs/${id}/parties`);
}

export async function postRepairPartyMessage(
  id: string,
  body: PostRepairPartyMessageBody,
): Promise<RepairPartyMessageResponse> {
  return apiFetch(`/landlord/api/repairs/${id}/party-messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Test Harness API
// ---------------------------------------------------------------------------

export interface HarnessActor {
  id: string;
  name: string;
}

export interface HarnessActors {
  landlord: HarnessActor;
  tenant: HarnessActor | null;
  vendors: HarnessActor[];
  assignedVendorId: string | null;
}

export async function getHarnessActors(ticketId: string): Promise<HarnessActors> {
  return apiFetch(`/landlord/api/repairs/${ticketId}/harness/actors`);
}

// ---------------------------------------------------------------------------
// Create Repair
// ---------------------------------------------------------------------------

export interface CreateRepairBody {
  title: string;
  description: string;
  category: 'plumbing' | 'electrical' | 'hvac' | 'appliance' | 'structural' | 'general';
  priority: 'emergency' | 'routine' | 'minor';
  unitId: string;
  tenantId?: string;
}

export async function createRepair(body: CreateRepairBody): Promise<{ repair: RepairRequest }> {
  return apiFetch('/landlord/api/repairs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// RA-ATT-LANDLORD-READ — live attachments (signed URLs) for the Repair Detail Photos card;
// derived from ListAttachmentsResponseSchema in @resida/api-types/repair.
export async function listRepairAttachments(id: string): Promise<ListAttachmentsResponse> {
  return apiFetch(`/landlord/api/repairs/${id}/attachments`);
}
